import React from 'react'
import { ContactSection } from '../components/landing/ContactSection'
import { FeatureGridSection } from '../components/landing/FeatureGridSection'
import { FinalCtaSection } from '../components/landing/FinalCtaSection'
import { LandingNav } from '../components/landing/LandingNav'
import { LandingFooter } from '../components/landing/SalesLandingSections'
import { ProductScreenVisual, type ProductScreenKey } from '../components/landing/ProductScreenVisual'
import { SeoHead } from '../components/landing/SeoHead'
import { UseCasesSection } from '../components/landing/UseCasesSection'
import { contactContent, content } from '../components/landing/landingContent'
import { nexusLandingContent } from '../components/landing/nexusLandingContent'
import {
  localizeMarketingPage,
  marketingPageBySlug,
  type MarketingPage,
  type MarketingPageLocale,
  type PublicMarketingLocale,
} from '../components/landing/marketingPages'
import {
  brandTitleForLocale,
  buildLocalizedStructuredData,
  canonicalUrl,
  runtimeCanonicalUrl,
  HTML_DIR,
  hreflangAlternates,
  localeSwitchDestination,
  marketingPathForLocale,
  OG_LOCALE,
  ogLocaleAlternates,
} from '../components/landing/marketingSeoUrls'
import { useI18n } from '../i18n/I18nContext'

type MarketingLandingPageProps = {
  slug: string
}

const appHref = '/login'

const renderRelatedLink = (slug: string, locale: MarketingPageLocale) => {
  const page = marketingPageBySlug.get(slug)
  if (!page) return null
  const localizedPage = localizeMarketingPage(page, locale)
  return <a key={slug} href={marketingPathForLocale(slug, locale as PublicMarketingLocale)}>{localizedPage.eyebrow}</a>
}

const pageChrome = {
  en: {
    home: 'Home', workflow: 'Built for the workflow buyers search for', useCases: 'Where this page fits',
    differentiators: 'Differentiators', why: 'Why teams choose NexusWorkforce360', faq: 'FAQ',
    questions: 'Common questions', related: 'Related pages', explore: 'Explore connected workforce workflows',
    signIn: 'Sign in', startFree: 'Start Free', howItWorks: 'See How It Works', demo: 'Contact Sales', network: 'Cross-organization workflow',
    networkTitle: 'One operating record across workforce relationships', onboarding: 'Implementation',
    onboardingTitle: 'Start with the organizations, locations, and workflows you already operate',
    finalTitle: 'See the workflow in your operating context', finalCopy: 'Discuss your organizations, locations, assignments, approvals, and reporting needs with the team.',
  },
  ar: {
    home: 'الرئيسية', workflow: 'قدرات تدعم سير العمل', useCases: 'لمن صُمم هذا الحل',
    differentiators: 'ما يميز المنصة', why: 'لماذا تختار فرق التشغيل رقيب 360؟', faq: 'الأسئلة الشائعة',
    questions: 'أسئلة المشترين حول هذا الحل', related: 'حلول ذات صلة', explore: 'استكشف مسارات مترابطة لإدارة القوى العاملة',
    signIn: 'تسجيل الدخول', startFree: 'ابدأ مجاناً', howItWorks: 'شاهد كيف تعمل', demo: 'تواصل مع المبيعات', network: 'تشغيل شبكة القوى العاملة',
    networkTitle: 'سجل تشغيلي موحد للعلاقات بين المؤسسات', onboarding: 'بدء التشغيل',
    onboardingTitle: 'ابدأ بالمؤسسات والمواقع ومسارات الاعتماد القائمة لديك',
    finalTitle: 'استعرض سير العمل في سياق عملياتك', finalCopy: 'ناقش احتياجات المؤسسات والمواقع والتعيينات والاعتمادات والتقارير مع فريق رقيب 360.',
  },
  fr: {
    home: 'Accueil', workflow: 'Fonctions adaptées au workflow', useCases: 'À qui s’adresse cette solution',
    differentiators: 'Différenciateurs', why: 'Pourquoi les équipes choisissent NexusWorkforce360', faq: 'FAQ',
    questions: 'Questions fréquentes', related: 'Solutions associées', explore: 'Explorer les workflows workforce associés',
    signIn: 'Se connecter', startFree: 'Commencer gratuitement', howItWorks: 'Voir comment ça marche', demo: 'Contacter les ventes', network: 'Workflow inter-organisation',
    networkTitle: 'Un registre opérationnel pour les relations workforce', onboarding: 'Mise en œuvre',
    onboardingTitle: 'Commencez avec vos organisations, sites et workflows existants',
    finalTitle: 'Découvrez le workflow dans votre contexte', finalCopy: 'Échangez sur vos organisations, sites, affectations, validations et besoins de reporting.',
  },
} as const

const PageSection: React.FC<{ title: string; copy: string }> = ({ title, copy }) => (
  <article className="landing-info-card">
    <h2>{title}</h2>
    <p>{copy}</p>
  </article>
)

const screenBySlug = (slug: string): ProductScreenKey => {
  if (slug.includes('document') || slug.includes('compliance')) return 'worker-documents'
  if (slug.includes('payroll') || slug.includes('timesheet')) return 'payroll-export'
  if (slug.includes('geofenced') || slug.includes('time-tracking') || slug.includes('security')) return 'geofence-map'
  if (slug.includes('staffing') || slug.includes('subcontractor') || slug.includes('recruitment')) return 'network-relationships'
  if (slug.includes('construction')) return 'jobs-orders'
  return 'network-dashboard'
}

export const MarketingLandingPage: React.FC<MarketingLandingPageProps> = ({ slug }) => {
  const { locale, t } = useI18n()
  const landingLocale = (locale === 'tr' ? 'en' : locale) as MarketingPageLocale
  const seoLocale = landingLocale as PublicMarketingLocale
  const basePage = marketingPageBySlug.get(slug) as MarketingPage
  const page = localizeMarketingPage(basePage, landingLocale)
  const chrome = pageChrome[seoLocale]
  const landingCopy = content[landingLocale]
  const displayTitle = brandTitleForLocale(page.title, seoLocale)
  const canonical = runtimeCanonicalUrl('marketing', page.slug, seoLocale)
  // Switching locale on a marketing page navigates to that locale's canonical
  // URL (crossing to Raqib for Arabic), keeping visible + canonical URLs aligned.
  const handleLocaleSwitch = (next: MarketingPageLocale) => {
    if (typeof window !== 'undefined') {
      window.location.href = localeSwitchDestination('marketing', page.slug, next as PublicMarketingLocale)
    }
  }
  const relatedSlugs = page.related
    .filter((relatedSlug, index, slugs) => slugs.indexOf(relatedSlug) === index)
    .slice(0, 6)
  const useCaseItems = page.useCases.map((useCase): [string, string] => [useCase, page.solution])

  return (
    <div className="landing-page">
      <SeoHead
        title={displayTitle}
        description={page.description}
        canonicalUrl={canonical}
        ogTitle={displayTitle}
        ogDescription={page.description}
        ogUrl={canonical}
        htmlLang={seoLocale}
        dir={HTML_DIR[seoLocale]}
        ogLocale={OG_LOCALE[seoLocale]}
        ogLocaleAlternates={ogLocaleAlternates('marketing', page.slug, seoLocale)}
        alternates={hreflangAlternates('marketing', page.slug)}
        jsonLd={buildLocalizedStructuredData({
          kind: 'marketing',
          slug: page.slug,
          locale: seoLocale,
          description: page.description,
          eyebrow: page.eyebrow,
          faq: page.faq,
        })}
      />
      <LandingNav
        copy={landingCopy}
        appHref={appHref}
        locale={landingLocale}
        onLocaleChange={handleLocaleSwitch}
      />
      <main id="top">
        <nav className="container landing-breadcrumbs" aria-label={chrome.related}>
          <a href={canonicalUrl('home', null, seoLocale)}>{chrome.home}</a>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{page.eyebrow}</span>
        </nav>
        <section className="landing-hero landing-hero--text">
          <div className="container landing-hero__grid">
            <div className="landing-hero__copy">
              <p className="landing-eyebrow">{page.eyebrow}</p>
              <h1>{page.h1}</h1>
              <p className="landing-lede">{page.intro}</p>
              <div className="landing-hero__actions">
                <a className="btn btn-primary" href="/login?mode=register">{chrome.startFree}</a>
                <a className="btn btn-secondary" href="#problem-solution">{chrome.howItWorks}</a>
              </div>
              <div className="landing-proof">
                {page.proof.map((item) => <span key={item}>{item}</span>)}
              </div>
            </div>
            <div className="landing-dashboard-preview landing-dashboard-preview--media" aria-label={`${page.eyebrow} product screen`}>
              <ProductScreenVisual variant={screenBySlug(page.slug)} className="landing-product-screen landing-product-screen--hero" />
            </div>
          </div>
        </section>

        <section className="landing-section landing-section--muted" id="problem-solution">
          <div className="container">
            <div className="landing-about-grid">
              <PageSection title={page.problemTitle} copy={page.problem} />
              <PageSection title={page.solutionTitle} copy={page.solution} />
            </div>
          </div>
        </section>

        <FeatureGridSection title={chrome.workflow} features={page.features} />
        <UseCasesSection title={chrome.useCases} items={useCaseItems} />

        <section className="landing-section landing-section--muted" id="network-workflow">
          <div className="container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">{chrome.network}</p>
              <h2>{chrome.networkTitle}</h2>
            </div>
            <p className="landing-lede">{page.solution}</p>
          </div>
        </section>

        <section className="landing-section" id="comparison">
          <div className="container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">{chrome.differentiators}</p>
              <h2>{chrome.why}</h2>
            </div>
            <div className="landing-gap-grid">
              {page.differentiators.map((item, index) => (
                <article className="landing-gap-card" key={item}>
                  <h3>{item}</h3>
                  <p>{page.features[index % page.features.length]}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-section landing-section--muted" id="faq">
          <div className="container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">{chrome.faq}</p>
              <h2>{chrome.questions}</h2>
            </div>
            <div className="landing-faq-list">
              {page.faq.map((item) => (
                <details className="landing-faq-item" key={item.question} open>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-section" id="related">
          <div className="container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">{chrome.related}</p>
              <h2>{chrome.explore}</h2>
            </div>
            <div className="landing-related-links">
              {relatedSlugs.map((relatedSlug) => renderRelatedLink(relatedSlug, landingLocale))}
            </div>
          </div>
        </section>

        {seoLocale !== 'ar' && <section className="landing-section" id="sales-decks" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
          <div className="container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">{t('Explore Interactive Presentations')}</p>
              <h2>{t('NexusWorkforce360 Sales Decks')}</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
              <a href="/sales-decks/interactive" style={{ padding: '1.5rem', borderRadius: '0.75rem', border: '2px solid rgb(16, 185, 129)', backgroundColor: 'rgba(16, 185, 129, 0.1)', textDecoration: 'none', color: 'inherit', transition: 'all 0.3s ease', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.2)'; e.currentTarget.style.borderColor = 'rgb(5, 150, 105)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.1)'; e.currentTarget.style.borderColor = 'rgb(16, 185, 129)'; }}>
                <span style={{ display: 'inline-block', backgroundColor: 'rgb(16, 185, 129)', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{t('FEATURED')}</span>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{t('Interactive Sales Deck')}</h3>
                <p style={{ fontSize: '0.875rem', color: 'rgba(148, 163, 184, 1)', marginBottom: '1rem' }}>{t('Live browser-based presentation with interactive simulators and real-time demonstrations.')}</p>
                <span style={{ color: 'rgb(16, 185, 129)', fontWeight: '600' }}>{t('Launch Presentation →')}</span>
              </a>
              <a href="/sales-decks" style={{ padding: '1.5rem', borderRadius: '0.75rem', border: '2px solid rgb(71, 85, 105)', backgroundColor: 'rgba(71, 85, 105, 0.1)', textDecoration: 'none', color: 'inherit', transition: 'all 0.3s ease', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(71, 85, 105, 0.2)'; e.currentTarget.style.borderColor = 'rgb(100, 116, 139)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(71, 85, 105, 0.1)'; e.currentTarget.style.borderColor = 'rgb(71, 85, 105)'; }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{t('Industry-Specific Decks')}</h3>
                <p style={{ fontSize: '0.875rem', color: 'rgba(148, 163, 184, 1)', marginBottom: '1rem' }}>{t('Printable, segment-specific decks for recruitment, construction, food & beverage, and security.')}</p>
                <span style={{ color: 'rgb(100, 116, 139)', fontWeight: '600' }}>{t('View All Decks →')}</span>
              </a>
            </div>
          </div>
        </section>}

        <section className="landing-section landing-section--muted" id="implementation">
          <div className="container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">{chrome.onboarding}</p>
              <h2>{chrome.onboardingTitle}</h2>
            </div>
            <p className="landing-lede">{page.problem}</p>
            <a className="btn btn-secondary" href="#contact">{chrome.demo}</a>
          </div>
        </section>

        <ContactSection copy={contactContent[landingLocale]} />
        <FinalCtaSection
          copy={{
            finalTitle: page.h1,
            finalCopy: page.intro,
            finalCta: chrome.startFree,
          }}
          appHref="/login?mode=register"
        />
      </main>
      <LandingFooter content={nexusLandingContent[landingLocale].footer} />
    </div>
  )
}
